/**
 * Checkpoint GraphQL Fragments
 *
 * Reusable fragments for Checkpoint primitive (35 exposed GraphQL fields).
 * Supports Formula #4 saga compensation pattern.
 */

import { gql } from '@apollo/client';

/**
 * Complete Checkpoint fields fragment
 *
 * NOTE: Fields with #[graphql(skip)] in checkpoint_model.rs are NOT exposed in GraphQL.
 * Those fields are internal cross-entity refs resolved via async resolvers.
 *
 * Skipped fields (internal): tenantId, transactionId, taskId, queueId,
 * parentCheckpointId, compensationTaskId, recoveredByActorId,
 * fullSnapshotCheckpointId, prerequisiteCheckpointIds, dependentCheckpointIds, createdBy
 *
 * Use the async resolvers instead: recoveredByActor, createdByActor
 *
 * Total: 35 fields (33 scalars/enums + 2 ActorSummary objects)
 */
export const CHECKPOINT_FIELDS = gql`
  fragment CheckpointFields on Checkpoint {
    # Identity (4 fields)
    id
    checkpointName
    entityType
    sequenceNumber

    # Runtime State (8 fields)
    currentStatus
    lastAccessedAt
    accessCount
    recoveryAttemptedAt
    recoverySuccess
    snapshotSizeBytes
    compressionRatio
    serializationDurationMs

    # Snapshot Data (6 fields)
    stateSnapshot
    snapshotFormat
    snapshotChecksum
    snapshotEncrypted
    deltaFromPrevious
    retentionDays
    autoArchiveAt

    # Saga Compensation (6 fields)
    checkpointType
    canRollback
    rollbackStrategy
    rollbackCount
    lastRollbackAt
    lastRollbackReason

    # Recovery Configuration (4 fields)
    resumeEnabled
    resumeTimeoutSeconds
    maxResumeAttempts
    rollbackOrder

    # Append-Only Architecture (3 fields)
    parentId
    originalId
    isActive

    # Lifecycle (3 fields)
    createdAt
    updatedAt
    metadata

    # Async Computed Resolvers (2 fields)
    compressedId
    recoveredByActor {
      id
      displayName
      role
    }
    createdByActor {
      id
      displayName
      role
    }
  }
`;

/**
 * Checkpoint statistics fields
 */
export const CHECKPOINT_STATISTICS_FIELDS = gql`
  fragment CheckpointStatisticsFields on CheckpointStatistics {
    totalCheckpoints
    availableCheckpoints
    rolledBackCheckpoints
    averageSnapshotSize
    totalStorageBytes
    averageRollbackCount
  }
`;

/**
 * Validation result fields
 */
export const VALIDATION_RESULT_FIELDS = gql`
  fragment ValidationResultFields on ValidationResult {
    valid
    checksumMatch
    snapshotIntact
    errors
  }
`;

/**
 * Rollback result fields
 */
export const ROLLBACK_RESULT_FIELDS = gql`
  fragment RollbackResultFields on RollbackResult {
    success
    checkpointId
    stateRestored
    compensationTasksExecuted
    rollbackDurationMs
  }
`;

/**
 * Resume result fields
 */
export const RESUME_RESULT_FIELDS = gql`
  fragment ResumeResultFields on ResumeResult {
    success
    checkpointId
    stateRestored
    workflowResumed
  }
`;
